import { useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import LoaderButton from 'components/Button/LoaderButton';
import { RegisterImplementationProps } from 'constants/types';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { getBackendUrl } from '../../../../services/get-backend-url';
import SignInBase from '../SignInBase';
import { AppLink } from 'components/Link/AppLink';
import { getProviderFormLabel } from '../utils';

const BACKEND_ROUTE = getBackendUrl();

interface LatticeConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
}

type OAuthState = {
  code: string | null;
  state: string | null;
  error: string | null;
};

const SignInLatticeOAuth = ({
  client,
  sandbox,
  onSubmit,
  setError,
  sessionKey,
  onMockOAuth,
  provider,
}: RegisterImplementationProps & AuthorizeContextValue) => {
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollingTimer = useRef<NodeJS.Timeout>(); // Reference to timer so we can clean it up

  const config: LatticeConfig = (client as any)?.latticeConfig;

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (pollingTimer.current) clearTimeout(pollingTimer.current!);
    },
    [],
  );

  const onCodeReceived = async (oAuthState?: OAuthState) => {
    if (oAuthState?.error) {
      setError({ message: oAuthState?.error, status: null });
      setLoading(false);
      return;
    }

    if (oAuthState?.code) {
      try {
        await onSubmit({
          providerRedirectUri: config.redirectUri,
          clientCode: oAuthState?.code,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  useQuery({
    queryKey: ['lattice_oauth_state', sessionKey],
    queryFn: () =>
      axios
        .get<{ oAuthState?: OAuthState }>(
          `${BACKEND_ROUTE}/auth/oauth/state/${sessionKey}`,
        )
        .then(async ({ data }) => {
          if (data.oAuthState) {
            setPolling(false);
            clearTimeout(pollingTimer.current!);
            await onCodeReceived(data.oAuthState);
          }

          return data;
        }),
    enabled: polling,
    // Poll OAuth state every second
    refetchInterval: 1000,
  });

  const handleSubmit = async () => {
    setLoading(true);
    const didMockOAuth = await onMockOAuth();

    if (didMockOAuth) {
      setLoading(false);
      return;
    }

    setPolling(true);
    pollingTimer.current = setTimeout(() => {
      setLoading(false);
      setPolling(false);
    }, 300_000); // Stop polling in 5 minutes

    const url = new URL('https://api-auth.latticehq.com/oauth2/auth');
    url.searchParams.set('client_id', config.clientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', config.redirectUri);
    url.searchParams.set('state', JSON.stringify({ sessionKey }));
    window.open(url.toString(), 'popup', 'popup=true');
  };

  const instructions = (
    <>
      <p>
        Lattice authentication will continue in a new window. Please make sure
        to allow popups and finish authenticating through Lattice in the new
        window.
      </p>
    </>
  );

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
    >
      <div
        style={{
          marginTop: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoaderButton
          isLoading={loading}
          disabled={loading}
          onClick={handleSubmit}
          text="Connect"
        />
      </div>
    </SignInBase>
  );
};

export default withAuthorizeContext(SignInLatticeOAuth);
